import React from "react";
import { ShieldCheck, Thermometer, Layers, ListChecks } from "lucide-react";

const NEON_CYAN  = "#00E5FF";
const NEON_GREEN = "#39FF14";
const NEON_AMBER = "#FFCC00";
const NEON_PINK  = "#FF2DAA";
const NEON_RED   = "#FF3B30";

const ZONES = [
  { id: 1, name: "Sıcak-Nemli (Hot-Humid)",       cities: "Antalya, Mersin, Adana, Hatay, Muğla",                color: NEON_RED },
  { id: 2, name: "Ilıman (Temperate)",            cities: "İstanbul, İzmir, Bursa, Trabzon, Balıkesir",          color: NEON_AMBER },
  { id: 3, name: "Soğuk (Cold)",                  cities: "Ankara, Konya, Eskişehir, Kayseri, Sivas",            color: NEON_GREEN },
  { id: 4, name: "Çok Soğuk (Very Cold)",         cities: "Erzurum, Kars, Ağrı, Van, Ardahan",                   color: NEON_CYAN },
  { id: 5, name: "En Soğuk (Extremely Cold)",     cities: "Hakkari (yüksek rakım), Ağrı (dağ)",                  color: NEON_PINK },
];

const U_VALUES = [
  { element: "Dış Duvar (U_wall)",         values: [0.70, 0.60, 0.50, 0.40, 0.35] },
  { element: "Çatı (U_roof)",              values: [0.45, 0.40, 0.30, 0.25, 0.20] },
  { element: "Taban / Döşeme (U_floor)",   values: [0.70, 0.60, 0.45, 0.40, 0.35] },
  { element: "Pencere (U_window)",         values: [2.40, 2.40, 2.00, 1.80, 1.80] },
];

const RULES = [
  "Isı yalıtımı hesaplamaları Derece-Gün yöntemine göre yapılır.",
  "Yoğuşma kontrolü (Glaser metodu) her yapı elemanı için zorunludur.",
  "Isı köprüleri minimize edilmelidir (ψ ≤ 0.01 W/mK).",
  "Hava sızıntısı n50 ≤ 4.0 h⁻¹ olmalıdır.",
  "Yenilenebilir enerji kullanımı teşvik edilir.",
  "Bina enerji performans sınıfı en az C olmalıdır.",
  "Isı yalıtım malzemesi λ (ısıl iletkenlik) değeri sertifikalı olmalıdır.",
];

function SectionHeader({ icon: Icon, n, title, color }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] font-extrabold px-2 py-0.5 border-2"
        style={{ color, borderColor: color, textShadow: `0 0 6px ${color}66` }}>
        § {n}
      </span>
      <Icon className="w-4 h-4" style={{ color }}/>
      <h3 className="font-mono text-sm font-extrabold uppercase tracking-[0.22em]"
        style={{ color, textShadow: `0 0 8px ${color}66` }}>
        {title}
      </h3>
    </div>
  );
}

export default function TS825View() {
  return (
    <section
      className="border-2 bg-black"
      style={{ borderColor: NEON_CYAN, boxShadow: `0 0 18px ${NEON_CYAN}33, inset 0 0 12px ${NEON_CYAN}11` }}
      data-testid="ts825-view">

      <header className="bg-[#0A0A0A] border-b-2 border-[#1F1F1F] px-5 py-4 flex items-center gap-3 flex-wrap">
        <ShieldCheck className="w-6 h-6" style={{ color: NEON_CYAN, filter: `drop-shadow(0 0 6px ${NEON_CYAN})` }}/>
        <div className="flex flex-col">
          <span className="font-mono text-base font-extrabold uppercase tracking-[0.22em]"
            style={{ color: NEON_CYAN, textShadow: `0 0 8px ${NEON_CYAN}88` }}>
            TS 825:2024
          </span>
          <span className="font-mono text-[12px] font-bold text-white">
            Binalarda Isı Yalıtım Kuralları · Thermal Insulation Rules for Buildings
          </span>
        </div>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 border-2"
          style={{ color: NEON_GREEN, borderColor: NEON_GREEN, textShadow: `0 0 4px ${NEON_GREEN}` }}>
          ◆ TÜRKİYE · ULUSAL STANDART
        </span>
      </header>

      <div className="p-5 space-y-8">

        {/* ── Section 1: Climate Zones ── */}
        <div>
          <SectionHeader icon={Thermometer} n="1" title="İklim Bölgeleri · Climate Zones" color={NEON_AMBER}/>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
            {ZONES.map((z) => (
              <div key={z.id}
                className="border-2 bg-[#050505] p-3 transition-all"
                style={{
                  borderColor: z.color + "55",
                  boxShadow: `0 0 8px ${z.color}22`,
                }}
                data-testid={`ts825-zone-${z.id}`}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 16px ${z.color}88, inset 0 0 8px ${z.color}33`; e.currentTarget.style.borderColor = z.color; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 8px ${z.color}22`; e.currentTarget.style.borderColor = z.color + "55"; }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono text-[20px] font-black"
                    style={{ color: z.color, textShadow: `0 0 8px ${z.color}88` }}>
                    {z.id}.
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-extrabold"
                    style={{ color: z.color }}>
                    Bölge
                  </span>
                </div>
                <div className="font-mono text-[12px] font-extrabold text-white leading-tight">
                  {z.name}
                </div>
                <div className="font-mono text-[10px] font-bold text-white/70 mt-2 leading-snug">
                  {z.cities}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section 2: U-Value Limits ── */}
        <div>
          <SectionHeader icon={Layers} n="2" title="U-Değer Limitleri · Maximum U-Values [W/m²K]" color={NEON_GREEN}/>
          <div className="overflow-x-auto border-2 border-[#1F1F1F]" data-testid="ts825-u-table">
            <table className="w-full font-mono text-[12px] font-bold" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#0A0A0A" }}>
                  <th className="text-left px-3 py-3 border-b-2"
                    style={{ borderColor: NEON_GREEN, color: NEON_GREEN, textShadow: `0 0 6px ${NEON_GREEN}66` }}>
                    Yapı Elemanı · Building Element
                  </th>
                  {[1, 2, 3, 4, 5].map((z) => (
                    <th key={z} className="text-center px-3 py-3 border-b-2 border-l border-[#1F1F1F]"
                      style={{ borderBottomColor: NEON_GREEN, color: NEON_GREEN, textShadow: `0 0 6px ${NEON_GREEN}66` }}>
                      {z}. Bölge
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {U_VALUES.map((row, i) => (
                  <tr key={row.element} style={{ background: i % 2 === 0 ? "#000" : "#050505" }}>
                    <td className="px-3 py-3 text-white border-b border-[#1F1F1F]">
                      {row.element}
                    </td>
                    {row.values.map((v, idx) => (
                      <td key={idx}
                        className="text-center px-3 py-3 border-b border-l border-[#1F1F1F]"
                        style={{
                          color: NEON_CYAN,
                          textShadow: `0 0 6px ${NEON_CYAN}55`,
                        }}>
                        {v.toFixed(2)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="font-mono text-[10px] font-bold text-white/60 mt-2 leading-snug">
            ◆ Lower U-value = better insulation. These are the maximum permitted values per TS 825:2024 Annex F.
          </div>
        </div>

        {/* ── Section 3: Technical Rules ── */}
        <div>
          <SectionHeader icon={ListChecks} n="3" title="Teknik Kurallar · Technical Rules" color={NEON_CYAN}/>
          <ul className="space-y-2" data-testid="ts825-rules">
            {RULES.map((rule, i) => (
              <li key={i}
                className="border-2 border-[#1F1F1F] bg-[#050505] px-3 py-2.5 flex items-start gap-3 transition-all"
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = NEON_CYAN + "88"; e.currentTarget.style.boxShadow = `0 0 8px ${NEON_CYAN}44`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1F1F1F"; e.currentTarget.style.boxShadow = "none"; }}>
                <span className="font-mono text-[11px] font-black px-1.5 py-0.5"
                  style={{ color: NEON_CYAN, textShadow: `0 0 6px ${NEON_CYAN}AA` }}>
                  ▸
                </span>
                <span className="font-mono text-[13px] font-bold text-white leading-snug">
                  {rule}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer */}
        <div className="border-2 border-[#1F1F1F] bg-[#050505] px-3 py-2">
          <p className="font-mono text-[10px] font-bold text-white/60 leading-snug">
            ◆ Excerpt summary for educational reference. For official binding requirements consult the
            full TS 825:2024 document via TSE (Türk Standartları Enstitüsü).
          </p>
        </div>
      </div>
    </section>
  );
}
